import React, { useCallback, useContext } from 'react';
import { AnchoredSheet, AnchoredSheetTitleBar } from '@shared/components/AnchoredSheet';
import { View } from '@shared/components/View';
import { ColorThemeContext, grid } from '@shared/styles';
import { API } from '@shared/types/api';
import { Trigger, useBoolean, useExecuteOnTrigger, useIsDesktop } from '@shared/util/hooks';
import { AuditTag } from './AuditTag';

const FIXED_WIDTH = { width: grid(46) };

interface Props {
  anchorName: string;
  label: string;
  tagOptions: API.AuditTagComponent['options'];
  isMultiselect: boolean;
  tags: string[];
  children: React.ReactNode;
  trigger: Trigger;
  onChange(selectedTags: string[]): void;
  isReadOnly?: boolean;
}

export function AuditTagModal({
  anchorName,
  tagOptions,
  label,
  isMultiselect,
  tags,
  children,
  trigger,
  onChange,
  isReadOnly,
}: Props) {
  const isDesktop = useIsDesktop();
  const colorTheme = useContext(ColorThemeContext);
  const [visible, showModal, hideModal] = useBoolean(false);

  useExecuteOnTrigger(trigger, showModal);

  const renderTitleBar = useCallback(() => {
    if (isDesktop) {
      return <AnchoredSheetTitleBar title={label} />;
    }
    return (
      <AnchoredSheetTitleBar
        title={label}
        rightIcon="exit"
        rightIconColor={colorTheme.foregroundActive}
        onRightIconPress={hideModal}
      />
    );
  }, [colorTheme.foregroundActive, label, hideModal, isDesktop]);

  const fixedWidth = isDesktop ? FIXED_WIDTH : undefined;

  return (
    <>
      <View onClick={isReadOnly ? undefined : showModal} style={{ maxWidth: '100%' }}>
        {children}
      </View>
      {visible && (
        <AnchoredSheet
          anchorName={anchorName}
          onClose={hideModal}
          renderTitleBar={renderTitleBar}
          style={fixedWidth}
        >
          <AuditTag
            tagOptions={tagOptions}
            tags={tags}
            isMultiselect={isMultiselect}
            onChange={onChange}
            onClose={hideModal}
          />
        </AnchoredSheet>
      )}
    </>
  );
}
